import { useEffect, useRef, useState } from "react";
import { Layout } from "../../components/layout/Layout";
import { SignaturePad } from "../../components/SignaturePad";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { toast } from "sonner";
import { UserCircle, Pencil, CalendarClock, Wallet, History, Send, Check, X as XIcon, Users, FileDown, Bell, Fingerprint } from "lucide-react";
import { isWebauthnAvailable, registerFingerprint, formatWebauthnError } from "../../lib/webauthn";

export const TYPE_CONGE_LABELS = { paye: "Congé payé", sans_solde: "Congé sans solde", special: "Congé spécial", rtt: "RTT" };
export const TYPE_DEMANDE_SALAIRE_LABELS = { acompte: "Acompte sur salaire", avance: "Avance sur salaire" };
export const STATUS_LABELS = { en_attente: "En attente", acceptee: "Acceptée", refusee: "Refusée", annulee: "Annulée" };
export const STATUS_VARIANTS = { en_attente: "secondary", acceptee: "default", refusee: "destructive", annulee: "outline" };

export function StatusBadge({ status, testId }) {
  return <Badge variant={STATUS_VARIANTS[status] || "secondary"} data-testid={testId}>{STATUS_LABELS[status] || status}</Badge>;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("fr-FR"); } catch { return iso; }
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
}

function ProfilTab({ user, magasin }) {
  return (
    <Card data-testid="profil-info-card">
      <CardContent className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground mb-1">Nom</p>
          <p className="font-medium" data-testid="profil-nom">{user.nom}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Prénom</p>
          <p className="font-medium" data-testid="profil-prenom">{user.prenom}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Poste</p>
          <p className="font-medium" data-testid="profil-poste">{user.poste}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Grade(s)</p>
          <p className="font-medium" data-testid="profil-grades">{(user.grades || []).join(", ") || "—"}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Magasin</p>
          <p className="font-medium" data-testid="profil-magasin">{magasin || "—"}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Téléphone</p>
          <p className="font-medium" data-testid="profil-telephone">{user.telephone || "—"}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Adresse e-mail</p>
          <p className="font-medium" data-testid="profil-email">{user.email || "—"}</p>
        </div>
        <p className="text-xs text-muted-foreground sm:col-span-2 mt-2">
          Votre profil est en lecture seule. Pour changer une de ces informations, utilisez l'onglet
          « Demande de modification » : la demande sera envoyée à un administrateur pour validation.
        </p>
      </CardContent>
    </Card>
  );
}

function ModificationTab({ user, onSubmitted }) {
  const [selected, setSelected] = useState({ nom: false, prenom: false, telephone: false, poste: false, email: false });
  const [values, setValues] = useState({ nom: user.nom || "", prenom: user.prenom || "", telephone: user.telephone || "", poste: user.poste || "", email: user.email || "" });
  const [commentaire, setCommentaire] = useState("");
  const [saving, setSaving] = useState(false);

  const FIELDS = [
    { key: "nom", label: "Nom" },
    { key: "prenom", label: "Prénom" },
    { key: "telephone", label: "Téléphone" },
    { key: "poste", label: "Poste" },
    { key: "email", label: "Adresse e-mail" },
  ];

  const submit = async () => {
    const champs = {};
    FIELDS.forEach((f) => { if (selected[f.key]) champs[f.key] = values[f.key]; });
    if (Object.keys(champs).length === 0) {
      toast.error("Sélectionnez au moins un champ à modifier");
      return;
    }
    setSaving(true);
    try {
      await api.post("/profil/personal-data-requests", { champs, commentaire });
      toast.success("Demande envoyée à l'administrateur");
      setSelected({ nom: false, prenom: false, telephone: false, poste: false, email: false });
      setCommentaire("");
      onSubmitted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="modification-form-card">
      <CardContent className="p-6 space-y-4">
        <p className="text-sm text-muted-foreground">
          Cochez les informations que vous souhaitez faire modifier, indiquez la nouvelle valeur, puis envoyez votre
          demande. Un administrateur devra l'accepter ou la refuser.
        </p>
        {FIELDS.map((f) => (
          <div key={f.key} className="flex items-center gap-3">
            <Checkbox
              data-testid={`mod-check-${f.key}`}
              checked={selected[f.key]}
              onCheckedChange={(v) => setSelected({ ...selected, [f.key]: !!v })}
            />
            <label className="w-24 text-sm font-medium shrink-0">{f.label}</label>
            <Input
              data-testid={`mod-input-${f.key}`}
              disabled={!selected[f.key]}
              value={values[f.key]}
              onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
            />
          </div>
        ))}
        <div>
          <label className="text-sm font-medium block mb-1">Commentaire (facultatif)</label>
          <Textarea data-testid="mod-commentaire" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} className="h-20" />
        </div>
        <Button onClick={submit} disabled={saving} className="gap-2" data-testid="mod-submit-button">
          <Send size={15} /> {saving ? "Envoi..." : "Envoyer la demande"}
        </Button>
      </CardContent>
    </Card>
  );
}

function FingerprintSection({ user }) {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/webauthn/credentials");
      setCredentials(data);
    } catch (e) {
      // silencieux : simple absence de droit, la section ne s'affiche pas de toute façon
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasPerm(user, "compte", "connexion_empreinte")) return null;

  const ajouter = async () => {
    if (!isWebauthnAvailable()) {
      toast.error("Votre appareil ou navigateur ne prend pas en charge la connexion par empreinte.");
      return;
    }
    setRegistering(true);
    try {
      const label = window.prompt("Nom de cet appareil (ex. \"Mon téléphone\")", "") || "";
      await registerFingerprint(label);
      toast.success("Empreinte enregistrée sur cet appareil");
      load();
    } catch (e) {
      toast.error(formatWebauthnError(e));
    } finally {
      setRegistering(false);
    }
  };

  const supprimer = async (id) => {
    if (!window.confirm("Retirer cette empreinte ? Vous ne pourrez plus l'utiliser pour vous connecter sur cet appareil.")) return;
    try {
      await api.delete(`/webauthn/credentials/${id}`);
      toast.success("Empreinte retirée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Card data-testid="fingerprint-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Fingerprint size={18} className="text-primary" />
          <p className="font-semibold">Connexion par empreinte digitale</p>
        </div>
        <p className="text-sm text-muted-foreground">
          Enregistrez l'empreinte digitale (ou Face ID) de cet appareil pour vous connecter sans matricule ni code
          PIN. L'empreinte reste stockée sur cet appareil uniquement — elle ne fonctionnera pas sur un autre
          téléphone ou ordinateur.
        </p>
        {!loading && credentials.length > 0 && (
          <div className="space-y-2">
            {credentials.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm" data-testid={`fingerprint-item-${c.id}`}>
                <span>{c.device_label}</span>
                <button
                  type="button" onClick={() => supprimer(c.id)}
                  className="text-muted-foreground hover:text-destructive" data-testid={`fingerprint-remove-${c.id}`}
                >
                  <XIcon size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        <Button onClick={ajouter} disabled={registering} variant="outline" className="gap-2" data-testid="fingerprint-add-button">
          <Fingerprint size={15} /> {registering ? "En cours..." : "Ajouter empreinte"}
        </Button>
      </CardContent>
    </Card>
  );
}

function AbsenceTab({ user, magasin, onSubmitted }) {
  const sigRef = useRef(null);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [typeConge, setTypeConge] = useState("paye");
  const [typeSpecial, setTypeSpecial] = useState("");
  const [saving, setSaving] = useState(false);
  const now = new Date().toLocaleString("fr-FR");

  const submit = async () => {
    if (!dateDebut || !dateFin) { toast.error("Choisissez une date de début et une date de fin"); return; }
    if (typeConge === "special" && !typeSpecial.trim()) { toast.error("Précisez le type de congé spécial"); return; }
    const signature_data = sigRef.current?.toDataURL() || "";
    if (!signature_data) { toast.error("Votre signature est obligatoire pour envoyer la demande"); return; }
    setSaving(true);
    try {
      await api.post("/profil/absences", {
        date_debut: dateDebut, date_fin: dateFin, type_conge: typeConge,
        type_conge_special: typeConge === "special" ? typeSpecial : "", signature_data,
      });
      toast.success("Demande d'absence envoyée");
      setDateDebut(""); setDateFin(""); setTypeConge("paye"); setTypeSpecial(""); sigRef.current?.clear();
      onSubmitted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="absence-form-card">
      <CardContent className="p-6 space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><p className="text-xs text-muted-foreground">Nom</p><p className="font-medium" data-testid="absence-auto-nom">{user.nom}</p></div>
          <div><p className="text-xs text-muted-foreground">Prénom</p><p className="font-medium" data-testid="absence-auto-prenom">{user.prenom}</p></div>
          <div><p className="text-xs text-muted-foreground">Magasin</p><p className="font-medium" data-testid="absence-auto-magasin">{magasin || "—"}</p></div>
          <div><p className="text-xs text-muted-foreground">Téléphone</p><p className="font-medium" data-testid="absence-auto-telephone">{user.telephone || "—"}</p></div>
        </div>
        <p className="text-xs text-muted-foreground">Date de la demande : <span className="font-medium">{now}</span> (renseignée automatiquement à l'envoi)</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium block mb-1">Date de début</label>
            <Input type="date" data-testid="absence-date-debut" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Date de fin</label>
            <Input type="date" data-testid="absence-date-fin" value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium block mb-2">Type de congé à utiliser</label>
          <RadioGroup value={typeConge} onValueChange={setTypeConge} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(TYPE_CONGE_LABELS).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm border rounded-lg px-3 py-2 cursor-pointer">
                <RadioGroupItem value={key} data-testid={`absence-type-${key}`} />
                {label}
              </label>
            ))}
          </RadioGroup>
          {typeConge === "special" && (
            <Input
              className="mt-3"
              placeholder="Précisez le type de congé spécial (mariage, décès, déménagement...)"
              value={typeSpecial}
              onChange={(e) => setTypeSpecial(e.target.value)}
              data-testid="absence-type-special-input"
            />
          )}
        </div>

        <div>
          <label className="text-sm font-medium block mb-2">Signature</label>
          <SignaturePad ref={sigRef} testId="absence-signature-pad" />
        </div>

        <Button onClick={submit} disabled={saving} className="gap-2" data-testid="absence-submit-button">
          <Send size={15} /> {saving ? "Envoi..." : "Envoyer la demande d'absence"}
        </Button>
      </CardContent>
    </Card>
  );
}

function AvanceTab({ user, magasin, onSubmitted }) {
  const sigRef = useRef(null);
  const [typeDemande, setTypeDemande] = useState("acompte");
  const [montant, setMontant] = useState("");
  const [moisPrelevement, setMoisPrelevement] = useState("");
  const [saving, setSaving] = useState(false);
  const now = new Date();
  const moisActuel = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const submit = async () => {
    const montantNum = Number(montant);
    if (!montantNum || montantNum <= 0) { toast.error("Saisissez un montant demandé valide"); return; }
    if (typeDemande === "avance" && !moisPrelevement) { toast.error("Choisissez le mois de prélèvement"); return; }
    const signature_data = sigRef.current?.toDataURL() || "";
    if (!signature_data) { toast.error("Votre signature est obligatoire pour envoyer la demande"); return; }
    setSaving(true);
    try {
      await api.post("/profil/avances", {
        type_demande: typeDemande, montant: montantNum,
        mois_prelevement: typeDemande === "avance" ? moisPrelevement : "", signature_data,
      });
      toast.success(`Demande ${typeDemande === "acompte" ? "d'acompte" : "d'avance"} envoyée`);
      setMontant(""); setMoisPrelevement(""); sigRef.current?.clear();
      onSubmitted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="avance-form-card">
      <CardContent className="p-6 space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><p className="text-xs text-muted-foreground">Nom</p><p className="font-medium">{user.nom}</p></div>
          <div><p className="text-xs text-muted-foreground">Prénom</p><p className="font-medium">{user.prenom}</p></div>
          <div><p className="text-xs text-muted-foreground">Magasin</p><p className="font-medium">{magasin || "—"}</p></div>
          <div><p className="text-xs text-muted-foreground">Téléphone</p><p className="font-medium">{user.telephone || "—"}</p></div>
        </div>
        <p className="text-xs text-muted-foreground">Date et heure de la demande renseignées automatiquement à l'envoi.</p>

        <div>
          <label className="text-sm font-medium block mb-2">Type de demande</label>
          <RadioGroup value={typeDemande} onValueChange={setTypeDemande} className="grid grid-cols-2 gap-3 max-w-md">
            {Object.entries(TYPE_DEMANDE_SALAIRE_LABELS).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm border rounded-lg px-3 py-2 cursor-pointer">
                <RadioGroupItem value={key} data-testid={`avance-type-${key}`} />
                {label}
              </label>
            ))}
          </RadioGroup>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium block mb-1">Montant demandé (€)</label>
            <Input type="number" min={0} data-testid="avance-montant" value={montant} onChange={(e) => setMontant(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Mois de prélèvement</label>
            {typeDemande === "acompte" ? (
              <Input value={moisActuel} disabled data-testid="avance-mois-auto" />
            ) : (
              <Input type="month" value={moisPrelevement} onChange={(e) => setMoisPrelevement(e.target.value)} data-testid="avance-mois-input" />
            )}
            <p className="text-[11px] text-muted-foreground mt-1">
              {typeDemande === "acompte" ? "Automatique : mois en cours (acompte)." : "À choisir vous-même (avance)."}
            </p>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium block mb-2">Signature</label>
          <SignaturePad ref={sigRef} testId="avance-signature-pad" />
        </div>

        <Button onClick={submit} disabled={saving} className="gap-2" data-testid="avance-submit-button">
          <Send size={15} /> {saving ? "Envoi..." : "Envoyer la demande"}
        </Button>
      </CardContent>
    </Card>
  );
}

function HistoriqueTab({ refreshKey }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    (async () => {
      const [mods, absences, avances] = await Promise.all([
        api.get("/profil/personal-data-requests"),
        api.get("/profil/absences"),
        api.get("/profil/avances"),
      ]);
      const all = [
        ...mods.data.map((r) => ({ ...r, kind: "modification" })),
        ...absences.data.map((r) => ({ ...r, kind: "absence" })),
        ...avances.data.map((r) => ({ ...r, kind: "avance" })),
      ].sort((a, b) => (b.date_demande || "").localeCompare(a.date_demande || ""));
      setItems(all);
    })();
  }, [refreshKey]);

  if (!items) return <p className="text-sm text-muted-foreground">Chargement...</p>;
  if (items.length === 0) return <p className="text-sm text-muted-foreground" data-testid="historique-empty">Aucune demande pour le moment.</p>;

  return (
    <div className="space-y-3" data-testid="historique-list">
      {items.map((it) => (
        <Card key={`${it.kind}-${it.id}`} data-testid={`historique-item-${it.kind}-${it.id}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="font-medium text-sm">
                {it.kind === "modification" && "Demande de modification des informations personnelles"}
                {it.kind === "absence" && `Demande d'absence — ${TYPE_CONGE_LABELS[it.type_conge] || it.type_conge}${it.type_conge === "special" && it.type_conge_special ? ` (${it.type_conge_special})` : ""}`}
                {it.kind === "avance" && `${TYPE_DEMANDE_SALAIRE_LABELS[it.type_demande] || it.type_demande} — ${it.montant} €`}
              </p>
              <StatusBadge status={it.status} testId={`historique-status-${it.kind}-${it.id}`} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Envoyée le {fmtDateTime(it.date_demande)}</p>
            {it.kind === "absence" && (
              <p className="text-xs text-muted-foreground">Du {fmtDate(it.date_debut)} au {fmtDate(it.date_fin)}</p>
            )}
            {it.kind === "avance" && (
              <p className="text-xs text-muted-foreground">Mois de prélèvement : {it.mois_prelevement}</p>
            )}
            {it.kind === "modification" && (
              <ul className="text-xs text-muted-foreground list-disc list-inside mt-1">
                {Object.entries(it.champs_demandes || {}).map(([k, v]) => <li key={k}>{k} → {v}</li>)}
              </ul>
            )}
            {it.status !== "en_attente" && it.status !== undefined && (
              <p className="text-xs mt-1">
                {it.status === "acceptee" && `Acceptée par ${it.decision_by_nom || "—"} le ${fmtDateTime(it.decision_at)}`}
                {it.status === "refusee" && `Refusée par ${it.decision_by_nom || "—"} le ${fmtDateTime(it.decision_at)}${it.motif_refus ? ` — ${it.motif_refus}` : ""}`}
                {it.status === "annulee" && `Annulée par ${it.cancelled_by_nom || "—"} le ${fmtDateTime(it.cancelled_at)}`}
              </p>
            )}
            {it.signature_decision && (
              <div className="mt-2">
                <p className="text-[11px] text-muted-foreground mb-1">Signature de validation :</p>
                <img src={it.signature_decision} alt="signature" className="h-12 border rounded bg-white" />
              </div>
            )}
            {(it.kind === "absence" || it.kind === "avance") && (
              <a
                href={`${api.defaults.baseURL}/profil/${it.kind === "absence" ? "absences" : "avances"}/${it.id}/pdf`}
                target="_blank"
                rel="noreferrer"
                className="inline-block mt-2"
                data-testid={`historique-pdf-${it.kind}-${it.id}`}
              >
                <Button size="sm" variant="outline" className="gap-1.5"><FileDown size={13} /> PDF</Button>
              </a>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AdminModerationTab({ refreshKey, bump }) {
  const [rows, setRows] = useState([]);
  const [decisionTarget, setDecisionTarget] = useState(null);
  const [commentaireAdmin, setCommentaireAdmin] = useState("");
  const [appliquer, setAppliquer] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/profil/personal-data-requests");
    setRows(data);
  };
  useEffect(() => { load(); }, [refreshKey]);

  const openDecision = (row) => { setDecisionTarget(row); setCommentaireAdmin(""); setAppliquer(true); };

  const decide = async (action) => {
    setSaving(true);
    try {
      await api.patch(`/profil/personal-data-requests/${decisionTarget.id}/decision`, {
        action, commentaire_admin: commentaireAdmin, appliquer,
      });
      toast.success(action === "acceptee" ? "Demande acceptée" : "Demande refusée");
      setDecisionTarget(null);
      load();
      bump();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const pending = rows.filter((r) => r.status === "en_attente");
  const treated = rows.filter((r) => r.status !== "en_attente");

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-semibold mb-2">En attente ({pending.length})</p>
        {pending.length === 0 && <p className="text-sm text-muted-foreground">Aucune demande en attente.</p>}
        <div className="space-y-3" data-testid="admin-mod-pending-list">
          {pending.map((r) => (
            <Card key={r.id} data-testid={`admin-mod-row-${r.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="font-medium text-sm">{r.prenom} {r.nom}</p>
                  <p className="text-xs text-muted-foreground">{fmtDateTime(r.date_demande)}</p>
                </div>
                <ul className="text-xs text-muted-foreground list-disc list-inside">
                  {Object.entries(r.champs_demandes || {}).map(([k, v]) => (
                    <li key={k}>{k} : « {r.champs_actuels?.[k] || "—"} » → « {v} »</li>
                  ))}
                </ul>
                {r.commentaire && <p className="text-xs italic text-muted-foreground">« {r.commentaire} »</p>}
                <div className="flex gap-2 pt-1">
                  <Button size="sm" className="gap-1" data-testid={`admin-mod-decide-${r.id}`} onClick={() => openDecision(r)}>
                    <Pencil size={13} /> Traiter
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      {treated.length > 0 && (
        <div>
          <p className="text-sm font-semibold mb-2">Historique</p>
          <div className="space-y-2">
            {treated.map((r) => (
              <Card key={r.id}>
                <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                  <p className="text-sm">{r.prenom} {r.nom} — {Object.keys(r.champs_demandes || {}).join(", ")}</p>
                  <StatusBadge status={r.status} />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!decisionTarget} onOpenChange={(o) => !o && setDecisionTarget(null)}>
        <DialogContent data-testid="admin-mod-decision-dialog">
          <DialogHeader><DialogTitle>Traiter la demande de {decisionTarget?.prenom} {decisionTarget?.nom}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <ul className="text-sm list-disc list-inside">
              {Object.entries(decisionTarget?.champs_demandes || {}).map(([k, v]) => <li key={k}>{k} → {v}</li>)}
            </ul>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={appliquer} onCheckedChange={(v) => setAppliquer(!!v)} data-testid="admin-mod-appliquer" />
              Appliquer directement ces changements à la fiche utilisateur si j'accepte
            </label>
            <Textarea placeholder="Commentaire (facultatif)" value={commentaireAdmin} onChange={(e) => setCommentaireAdmin(e.target.value)} data-testid="admin-mod-commentaire" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="gap-1" disabled={saving} onClick={() => decide("refusee")} data-testid="admin-mod-refuse-button">
              <XIcon size={14} /> Refuser
            </Button>
            <Button className="gap-1" disabled={saving} onClick={() => decide("acceptee")} data-testid="admin-mod-accept-button">
              <Check size={14} /> Accepter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NotificationsTab({ user }) {
  const [catalog, setCatalog] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [cat, pr] = await Promise.all([
      api.get("/profil/notification-catalog"),
      api.get("/profil/notification-prefs"),
    ]);
    setCatalog(cat.data.catalog);
    setPrefs(pr.data);
  };
  useEffect(() => { load(); }, []);

  const isVisible = (n) => {
    if (n.role === "demandeur") return true;
    if (user.is_admin) return true;
    if (n.admin_only) return false;
    if (n.requires_permission) return hasPerm(user, n.requires_permission);
    return false;
  };

  const visible = catalog.filter(isVisible);
  const categories = [...new Set(visible.map((n) => n.category))];

  const toggle = (id) => setPrefs((p) => ({ ...p, [id]: !p[id] }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profil/notification-prefs", { prefs });
      toast.success("Préférences de notifications enregistrées");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (visible.length === 0) {
    return <p className="text-sm text-muted-foreground">Aucune notification ne vous concerne pour le moment.</p>;
  }

  return (
    <Card data-testid="notifications-prefs-card">
      <CardContent className="p-6 space-y-5">
        <p className="text-sm text-muted-foreground">
          Choisissez les notifications par e-mail que vous souhaitez recevoir. Décochez celles que vous ne
          souhaitez plus recevoir — les autres restent envoyées normalement.
        </p>
        {categories.map((cat) => (
          <div key={cat} className="space-y-2">
            <p className="text-sm font-semibold">{cat}</p>
            <div className="space-y-2">
              {visible.filter((n) => n.category === cat).map((n) => (
                <label key={n.id} className="flex items-start gap-2.5 text-sm cursor-pointer">
                  <Checkbox
                    className="mt-0.5"
                    checked={!!prefs[n.id]}
                    onCheckedChange={() => toggle(n.id)}
                    data-testid={`notif-pref-${n.id}`}
                  />
                  <span>
                    {n.label}
                    {n.role === "decisionnaire" && (
                      <span className="block text-[11px] text-muted-foreground leading-tight mt-0.5">
                        Vous recevez ceci en tant que personne habilitée à traiter ce type de demande.
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
        <Button onClick={save} disabled={saving} className="gap-2" data-testid="notif-prefs-save">
          <Send size={15} /> {saving ? "Enregistrement..." : "Enregistrer mes préférences"}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function ProfilPage() {
  const { user } = useAuth();
  const [magasin, setMagasin] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    (async () => {
      if (!user?.effective_shop_id) return;
      const { data } = await api.get("/shops");
      const shop = data.find((s) => s.id === user.effective_shop_id);
      setMagasin(shop?.nom || "");
    })();
  }, [user?.effective_shop_id]);

  if (!user) return null;

  return (
    <Layout title="Profil et demande">
      <div className="max-w-3xl mx-auto">
        <Tabs defaultValue="profil">
          <TabsList className="mb-5 flex-wrap h-auto">
            <TabsTrigger value="profil" data-testid="profil-tab-profil" className="gap-1.5"><UserCircle size={14} /> Mon profil</TabsTrigger>
            <TabsTrigger value="modification" data-testid="profil-tab-modification" className="gap-1.5"><Pencil size={14} /> Demande de modification</TabsTrigger>
            <TabsTrigger value="absence" data-testid="profil-tab-absence" className="gap-1.5"><CalendarClock size={14} /> Demande d'absence</TabsTrigger>
            <TabsTrigger value="avance" data-testid="profil-tab-avance" className="gap-1.5"><Wallet size={14} /> Acompte / Avance</TabsTrigger>
            <TabsTrigger value="historique" data-testid="profil-tab-historique" className="gap-1.5"><History size={14} /> Mes demandes</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="profil-tab-notifications" className="gap-1.5"><Bell size={14} /> Notifications</TabsTrigger>
            {user.is_admin && (
              <TabsTrigger value="admin" data-testid="profil-tab-admin" className="gap-1.5"><Users size={14} /> Demandes à traiter</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="profil"><ProfilTab user={user} magasin={magasin} /></TabsContent>
          <TabsContent value="modification">
            <ModificationTab user={user} onSubmitted={bump} />
            <div className="mt-4"><FingerprintSection user={user} /></div>
          </TabsContent>
          <TabsContent value="absence"><AbsenceTab user={user} magasin={magasin} onSubmitted={bump} /></TabsContent>
          <TabsContent value="avance"><AvanceTab user={user} magasin={magasin} onSubmitted={bump} /></TabsContent>
          <TabsContent value="historique"><HistoriqueTab refreshKey={refreshKey} /></TabsContent>
          <TabsContent value="notifications"><NotificationsTab user={user} /></TabsContent>
          {user.is_admin && (
            <TabsContent value="admin"><AdminModerationTab refreshKey={refreshKey} bump={bump} /></TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}
